import {DatePicker, Input, Segmented, Select, Spin} from 'antd'
import FieldsFactory from 'components/form/FieldsFactory'
import {ResetButton, SubmitButton} from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik, useFormikContext} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useEffect, useMemo, useState} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {PiCalendarDotsDuotone, PiCalendarSlashDuotone} from 'react-icons/pi'
import {compose, mapProps, pure} from 'recompose'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import {lookup_getConstants_Api, timetable_getDetails_Api} from '../../../../apis'
import {formatDateString, getDateString, parseDayjs} from '../../../../helpers/datetimeHelpers'
import {gConfigs} from '../../configs'
import {SelectWidget} from '../../custom/ReactSelectComponents'
import dayjs from 'dayjs'
import {ActionModalWrapper} from '../../../../components/form/ActionModalWrapper'
import {appClassNames} from '../../custom/appClassNames'
import AsyncSelect from '../../../../components/form/AsyncSelect'
import useAsyncAction from '../../../../modules/asyncCache/useAsyncAction'
import {getResponseItems} from '../../../Shared'
import Translate from '../../../../modules/local/Translate'

const AsyncSelectWidget = ({children}) => {
  const params = {
    apiInfo: lookup_getConstants_Api,
    query: bindQueryParam({type: 'absence_reasons'}),
    cacheId: lookup_getConstants_Api?.path + 'absence_reasons',
  }
  return <AsyncSelect {...params}>{children}</AsyncSelect>
}

const formatSessionLabel = (detail) => {
  if (!detail?.start_time) return '—'
  const start = dayjs(detail.start_time)
  const end = detail.end_time ? dayjs(detail.end_time) : null
  const datePart = start.format('DD/MM/YYYY')
  const timePart = end
    ? `${start.format('HH:mm')} – ${end.format('HH:mm')}`
    : start.format('HH:mm')
  return `${datePart} · ${timePart}`
}

const SessionPickerField = ({course_class_id, name, value, onChange}) => {
  const t = useTranslate()
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(false)

  const {handleAsyncAction: loadSessions} = useAsyncAction({
    apiInfo: timetable_getDetails_Api,
    onSuccess: (__, data) => {
      const items = getResponseItems(data?.response) || []
      const upcoming = items
        .filter((s) => dayjs(s.start_time).isAfter(dayjs().subtract(1, 'day')))
        .sort((a, b) => dayjs(a.start_time).unix() - dayjs(b.start_time).unix())
      setSessions(upcoming)
      setLoading(false)
    },
    onError: () => setLoading(false),
  })

  useEffect(() => {
    if (course_class_id) {
      setLoading(true)
      loadSessions({owner_type: 'course_class', owner_id: course_class_id}, {})
    }
  }, [course_class_id])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spin size="small"/>
      </div>
    )
  }

  if (!sessions.length) {
    return (
      <Translate>
        {(t) => (
          <div className="py-3 text-center text-xs text-color-400 italic">
            {t('no upcoming sessions found')}
          </div>
        )}
      </Translate>
    )
  }

  return (
    <Select
      className="w-full"
      placeholder={t('choose session')}
      value={value || undefined}
      onChange={(val) => onChange(createValue(name, val))}
      options={sessions.map((s) => ({
        value: s.id,
        label: formatSessionLabel(s),
      }))}
    />
  )
}

const formSchema = {
  reason_group: {
    title: 'reason type',
    children: [
      {
        name: 'reason_group',
        isRequired: true,
        component: compose(
          withTranslate,
          (WrappedComponent) => (props) => (
            <AsyncSelectWidget>
              {({items, isLoading}) => (
                <WrappedComponent
                  {...props}
                  options={items}
                />
              )}
            </AsyncSelectWidget>
          ),
          mapProps(({name, value, translate, onChange, options}) => ({
            name,
            value,
            allowClear: true,
            className: 'w-full',
            placeholder: translate('click to select'),
            options,
            onChange: (value) => onChange(createValue(name, value)),
          })),
          pure
        )(SelectWidget),
      },
    ],
  },

  reason: {
    title: 'detail',
    children: [
      {
        name: 'reason',
        component: createControlledFormField({
          rows: 3,
          placeholder: 'content details',
          Component: Input.TextArea,
        }),
      },
    ],
  },

  started_finished_date: {
    inline: true,
    className: appClassNames.inline_2,
    children: [
      {
        name: 'from_date',
        label: 'from date',
        component: compose(
          withTranslate,
          mapProps(({name, value, onChange, translate}) => ({
            name,
            value,
            className: 'w-full',
            format: gConfigs.dateFormat,
            suffixIcon: <IoCalendarOutline className="text-color-500"/>,
            placeholder: translate('ex: 20/10/2010'),
            onChange: (date) => {
              onChange(createValue(name, date ? dayjs(new Date(date)) : null))
            },
          }))
        )(DatePicker),
      },
      {
        name: 'to_date',
        label: 'to date',
        component: compose(
          withTranslate,
          mapProps(({name, value, onChange, translate}) => ({
            name,
            value,
            className: 'w-full',
            format: gConfigs.dateFormat,
            suffixIcon: <IoCalendarOutline className="text-color-500"/>,
            placeholder: translate('ex: 20/10/2010'),
            onChange: (date) => {
              onChange(createValue(name, date ? dayjs(new Date(date)) : null))
            },
          }))
        )(DatePicker),
      },
    ],
  },
}

const AbsenceTypeToggle = () => {
  const t = useTranslate()
  const {values, setFieldValue} = useFormikContext()
  const absenceType = values?.absence_type ?? 2

  return (
    <Segmented
      block
      value={absenceType}
      onChange={(val) => {
        setFieldValue('absence_type', val)
        if (val === 1) {
          setFieldValue('from_date', null)
          setFieldValue('to_date', null)
        } else {
          setFieldValue('schedule_id', null)
          setFieldValue('from_date', dayjs())
          setFieldValue('to_date', dayjs())
        }
      }}
      options={[
        {
          value: 1,
          label: (
            <div className="flex items-center justify-center gap-1.5 py-0.5">
              <PiCalendarDotsDuotone size={14}/>
              <span className="text-xs font-medium">{t('choose session')}</span>
            </div>
          ),
        },
        {
          value: 2,
          label: (
            <div className="flex items-center justify-center gap-1.5 py-0.5">
              <PiCalendarSlashDuotone size={14}/>
              <span className="text-xs font-medium">{t('choose date')}</span>
            </div>
          ),
        },
      ]}
    />
  )
}

const SessionField = () => {
  const {values, setFieldValue} = useFormikContext()
  const course_class_id = values?.course_class_id
  const schedule_id = values?.schedule_id

  return (
    <SessionPickerField
      course_class_id={course_class_id}
      name="schedule_id"
      value={schedule_id}
      onChange={({target}) => setFieldValue('schedule_id', target.value)}
    />
  )
}

const AbsenceTypeFields = () => {
  const {values} = useFormikContext()
  const absenceType = values?.absence_type ?? 2

  if (absenceType === 1) {
    return (
      <div className="flex flex-col gap-1">
        <SessionField/>
      </div>
    )
  }

  return (
    <FieldsFactory
      formSchema={[formSchema.started_finished_date]}
    />
  )
}

export const CourseClassAbsenceRequestCreateForm = ({
  initialValues,
  onSubmit = NullAsDefault,
  isSubmitting = false,
}) => {
  const t = useTranslate()

  const _initialValues = useMemo(
    () => ({
      absence_type: 2,
      from_date: dayjs(),
      to_date: dayjs(),
      reason: undefined,
      reason_group: 'health',
      schedule_id: undefined,
      ...(initialValues ?? {}),
    }),
    [initialValues]
  )

  const validate = (values) => {
    const errors = {}
    const {absence_type, from_date, to_date, reason_group, schedule_id} = values ?? {}

    if (absence_type === 1) {
      if (!schedule_id) errors.schedule_id = t('required field')
    } else {
      if (_.isEmpty(from_date ?? '')) errors.from_date = t('required field')
      if (_.isEmpty(to_date ?? '')) errors.to_date = t('required field')
    }
    if (_.isEmpty(reason_group ?? '')) errors.reason_group = t('required field')

    return errors
  }

  return (
    <Formik
      validate={validate}
      validateOnMount={true}
      validateOnChange={true}
      enableReinitialize={true}
      initialValues={_initialValues}
      onSubmit={(values) => {
        const {from_date, to_date, course_classes, ...rest} = values ?? {}
        onSubmit(
          _.omitBy(
            {
              ...rest,
              ...(values.absence_type !== 1
                ? {
                    from_date: formatDateString(from_date),
                    to_date: formatDateString(to_date),
                  }
                : {}),
            },
            _.isUndefined
          )
        )
      }}>
      <ActionModalWrapper
        className="gap-4"
        action={
          <div className="flex items-center justify-end gap-2 p-2 background">
            <ResetButton disabled={isSubmitting}/>
            <SubmitButton
              label="create"
              dirtyRequired={false}
              loading={isSubmitting}
            />
          </div>
        }>
        <div className="flex flex-col gap-4">
          {/* Type toggle */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-color-300 uppercase tracking-wide">
              {t('absence type')}
            </span>
            <AbsenceTypeToggle/>
          </div>

          {/* Dynamic fields based on absence_type */}
          <AbsenceTypeFields/>

          {/* Reason fields — always shown */}
          <FieldsFactory
            formSchema={[
              formSchema.reason_group,
              formSchema.reason,
            ]}
          />
        </div>
      </ActionModalWrapper>
    </Formik>
  )
}

export const CourseClassAbsenceRequestUpdateForm = ({
  initialValues,
  onSubmit = NullAsDefault,
  isSubmitting = false,
}) => {
  const t = useTranslate()

  const _initialValues = useMemo(() => {
    const {to_date, from_date, ...rest} = initialValues ?? {}
    return {
      absence_type: 2,
      from_date: parseDayjs(from_date),
      to_date: parseDayjs(to_date),
      ...rest,
    }
  }, [initialValues])

  const validate = (values) => {
    const errors = {}
    const {absence_type, from_date, to_date, reason_group, schedule_id} = values ?? {}

    if (absence_type === 1) {
      if (!schedule_id) errors.schedule_id = t('required field')
    } else {
      if (_.isEmpty(from_date ?? '')) errors.from_date = t('required field')
      if (_.isEmpty(to_date ?? '')) errors.to_date = t('required field')
    }
    if (_.isEmpty(reason_group ?? '')) errors.reason_group = t('required field')

    return errors
  }

  return (
    <Formik
      validate={validate}
      validateOnMount={true}
      validateOnChange={true}
      enableReinitialize={true}
      initialValues={_initialValues}
      onSubmit={(values) => {
        const changes = shallowDiff(values, _initialValues)
        const {from_date, to_date, ...rest} = changes ?? {}
        onSubmit(
          _.omitBy(
            {
              ...rest,
              ...(values.absence_type !== 1
                ? {
                    to_date: getDateString(values.to_date),
                    from_date: getDateString(values.from_date),
                  }
                : {}),
            },
            _.isUndefined
          )
        )
      }}>
      <ActionModalWrapper
        className="gap-4"
        action={
          <div className="flex items-center justify-end gap-2 p-2 background">
            <ResetButton disabled={isSubmitting}/>
            <SubmitButton
              label="save"
              dirtyRequired={false}
              loading={isSubmitting}
            />
          </div>
        }>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-color-300 uppercase tracking-wide">
              {t('absence type')}
            </span>
            <AbsenceTypeToggle/>
          </div>
          <AbsenceTypeFields/>
          <FieldsFactory
            formSchema={[
              formSchema.reason_group,
              formSchema.reason,
            ]}
          />
        </div>
      </ActionModalWrapper>
    </Formik>
  )
}
